import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  Select as MuiSelect,
  MenuItem,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { Search as SearchIcon, Check as CheckIcon, Description as DescriptionIcon } from "@mui/icons-material";
import { useNavigation } from "contexts/notificationsContext";
import { IApontamento, ISalvarApontamento, salvarApontamento, listarComponentesAplicaveis, listarGruposComponentesAplicaveis, IComponenteAplicavel } from "services/modules/aplicabilidades";
import { StripedDataGrid } from "components/StripedDataGrid";
import { TIME_DEBOUNCE } from "hooks/usePagination";
import { debounce } from "lodash";

interface ModalApontamentoProps {
  open: boolean;
  onClose: () => void;
  apontamento?: IApontamento;
  idEmpresa: number;
  idComponenteBase: number;
  codigoBase: string;
  // Frente 10 (02/10/2026): no modo "agrupar componentes" (isAgrupamento && !isAgrupamentoBases)
  // o modal seleciona VÁRIOS códigos — mesmo padrão do ModalAgrupamentoBases — e onSave recebe
  // um array. Nos demais usos (escolher o aplicável do cartão; editar apontamento existente)
  // segue a seleção única com o botão APLICAR.
  isAgrupamento?: boolean;
  isAgrupamentoBases?: boolean;
  // Ids de componentes já agrupados no apontamento — ficam fora da lista no modo múltiplo
  // (o backend recusa duplicado com "Este componente já está agrupado a este apontamento.").
  idsJaAgrupados?: number[];
  onSave: (selecao?: IComponenteAplicavel | IComponenteAplicavel[]) => void | Promise<void>;
}

export const ModalApontamento: React.FC<ModalApontamentoProps> = ({
  open,
  onClose,
  apontamento,
  idEmpresa,
  idComponenteBase,
  codigoBase,
  isAgrupamento,
  isAgrupamentoBases,
  idsJaAgrupados,
  onSave,
}) => {
  const { openNotification } = useNavigation();
  const [isLoading, setIsLoading] = useState(false);
  const [componentesAplicaveis, setComponentesAplicaveis] = useState<IComponenteAplicavel[]>([]);
  const [componenteSelecionado, setComponenteSelecionado] = useState<IComponenteAplicavel | null>(null);
  // Frente 10 (02/10/2026): seleção múltipla só no agrupamento de componentes. Guarda o OBJETO
  // (não só o id) porque a lista é paginada no servidor — preserva a seleção entre páginas.
  const modoMultiplo = !!isAgrupamento && !isAgrupamentoBases;
  const [componentesSelecionados, setComponentesSelecionados] = useState<Map<number, IComponenteAplicavel>>(new Map());
  const [filtroBusca, setFiltroBusca] = useState("");
  const [filtroGrupo, setFiltroGrupo] = useState<string>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  // TAREFA 05 (FASE 03): grupos carregados uma única vez do backend (TODOS os apontáveis),
  // não derivados apenas da página atual do grid.
  const [gruposDisponiveis, setGruposDisponiveis] = useState<string[]>([]);

  // Opções do dropdown Grupo a partir da lista completa carregada do backend.
  const gruposOpcoes = useMemo(() => {
    return [{ value: "", label: "Todos" }, ...gruposDisponiveis.map((g) => ({ value: g, label: g }))];
  }, [gruposDisponiveis]);

  // Debounced search
  const debouncedFiltroBusca = useCallback(
    debounce((value: string) => {
      setFiltroBusca(value);
      setPage(1);
    }, TIME_DEBOUNCE),
    []
  );

  useEffect(() => {
    if (open) {
      // Resetar estados ao abrir (exceto se estiver editando)
      if (!apontamento) {
        setFiltroBusca("");
        setFiltroGrupo("");
        setComponenteSelecionado(null);
        setComponentesSelecionados(new Map());
      }
      setPage(1);
    }
  }, [open, idEmpresa, idComponenteBase, apontamento]);

  useEffect(() => {
    if (open) {
      carregarComponentesAplicaveis();
    }
  }, [open, filtroBusca, filtroGrupo, page, pageSize]);

  // TAREFA 05 (FASE 03): carrega os grupos uma única vez ao abrir o modal (não a cada página/busca),
  // garantindo o dropdown Grupo completo independentemente da paginação server-side.
  useEffect(() => {
    if (!open) return;
    let ativo = true;
    (async () => {
      try {
        const grupos = await listarGruposComponentesAplicaveis(idEmpresa, idComponenteBase);
        if (ativo) setGruposDisponiveis(grupos);
      } catch (error) {
        console.error("Erro ao carregar grupos de componentes aplicáveis:", error);
        if (ativo) setGruposDisponiveis([]);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [open, idEmpresa, idComponenteBase]);

  const carregarComponentesAplicaveis = async () => {
    setIsLoading(true);
    // FASE 03 (item 5): limpa a lista antes de cada busca (abertura, troca de filtro/página) para
    // não exibir os registros do filtro anterior enquanto a nova consulta não chega.
    setComponentesAplicaveis([]);
    try {
      const response = await listarComponentesAplicaveis(
        idEmpresa,
        idComponenteBase,
        filtroBusca || undefined,
        undefined,
        page,
        pageSize,
        filtroGrupo || undefined
      );
      
      // Frente 10 (02/10/2026): no modo múltiplo, esconde os componentes já agrupados no
      // apontamento (remoção dentro da página corrente é cosmética; o total segue o servidor).
      const ocultar = modoMultiplo ? new Set<number>(idsJaAgrupados || []) : new Set<number>();
      const componentes = ((response.data.items || []) as IComponenteAplicavel[]).filter(
        (c) => !ocultar.has(c.idComponente)
      );

      setComponentesAplicaveis(componentes);
      setTotalRecords(response.data.totalRecords || 0);
      
      // Se estiver editando, selecionar o componente atual
      if (apontamento) {
        const componenteAtual = componentes.find((c: IComponenteAplicavel) => c.idComponente === apontamento.idComponenteAplicavel);
        if (componenteAtual) {
          setComponenteSelecionado(componenteAtual);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar componentes aplicáveis:", error);
      openNotification({ message: "Erro ao carregar componentes aplicáveis", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // Frente 10 (02/10/2026): alterna a seleção de um código no modo múltiplo.
  const handleToggleSelecao = (componente: IComponenteAplicavel) => {
    setComponentesSelecionados((prev) => {
      const novo = new Map(prev);
      if (novo.has(componente.idComponente)) {
        novo.delete(componente.idComponente);
      } else {
        novo.set(componente.idComponente, componente);
      }
      return novo;
    });
  };

  // Frente 10 (02/10/2026): CONTINUAR (n) — devolve todos os selecionados de uma vez; quem
  // enfileira uma operação por item é o DetalheComponente (handleAdicionarComponenteAgrupado).
  const handleContinuarMultiplo = async () => {
    if (componentesSelecionados.size < 1) {
      openNotification({ message: "Selecione pelo menos um componente", type: "error" });
      return;
    }
    setIsLoading(true);
    try {
      await onSave(Array.from(componentesSelecionados.values()));
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!componenteSelecionado) {
      openNotification({ message: "Selecione um componente aplicável", type: "error" });
      return;
    }

    // Se não há apontamento, é um item em edição - apenas atualizar sem salvar no backend
    if (!apontamento) {
      // TAREFA 13.3 (FASE 03): travar o botão APLICAR durante o onSave evita double-submit
      // (dois POSTs paralelos que estouravam o índice UNIQUE → 500).
      setIsLoading(true);
      try {
        await onSave(componenteSelecionado);
        onClose();
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Se há apontamento, salvar no backend
    setIsLoading(true);
    try {
      const data: ISalvarApontamento = {
        idAplicabilidade: apontamento.idAplicabilidade,
        idEmpresa,
        idComponenteBase,
        idComponenteAplicavel: componenteSelecionado.idComponente,
        isPadrao: false, // Será gerenciado na tela principal
        observacao: undefined,
      };

      await salvarApontamento(data);
      openNotification({ message: "Apontamento salvo com sucesso", type: "success" });
      await onSave();
      onClose();
    } catch (error: any) {
      const errorMessage = 
        error?.data?.errors?.Mensagens?.[0] ||
        error?.response?.data?.errors?.Mensagens?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Erro ao salvar apontamento";
      openNotification({ message: errorMessage, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // Função para construir URL da imagem
  const buildImageSrc = (icone?: string | null): string => {
    if (!icone) return "";
    const trimmed = icone.trim();
    if (!trimmed) return "";
    
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      return trimmed;
    }
    
    if (trimmed.startsWith("/")) {
      return `${process.env.REACT_APP_API_URL || "http://localhost:3099"}${trimmed}`;
    }
    
    return `${process.env.REACT_APP_API_URL || "http://localhost:3099"}/${trimmed}`;
  };

  const columns: GridColDef[] = [
    {
      field: "imagem",
      headerName: "",
      width: 80,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        const imageUrl = buildImageSrc(params.row.icone);
        return (
          <Box
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#f5f5f5",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={params.row.codigo}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <Box sx={{ width: "100%", height: "100%", bgcolor: "#f5f5f5" }} />
            )}
          </Box>
        );
      },
    },
    {
      field: "codigo",
      headerName: "Código",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "cor",
      headerName: "Cor",
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">{params.value || "-"}</Typography>
      ),
    },
    {
      field: "descricao",
      headerName: "Descrição",
      flex: 2,
      minWidth: 180,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: "normal",
            lineHeight: 1.3,
            py: 0.5,
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "grupo",
      headerName: "Grupo",
      width: 150,
      // FASE 05 S-02: fallback "-" para grupos vazios/null (defesa em camada UI).
      renderCell: (params) => (
        <Typography variant="body2">{params.value || "-"}</Typography>
      ),
    },
    {
      field: "referencia",
      headerName: "Referência",
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2">{params.value || "MERCADO"}</Typography>
      ),
    },
    {
      field: "acoes",
      headerName: "Ações",
      width: 120,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        const isSelected = modoMultiplo
          ? componentesSelecionados.has(params.row.idComponente)
          : componenteSelecionado?.idComponente === params.row.idComponente;
        const fichaTecnica = params.row.fichaTecnica;
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {fichaTecnica && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(fichaTecnica, "_blank", "noopener,noreferrer");
                }}
                title="Visualizar ficha técnica"
              >
                <DescriptionIcon fontSize="small" sx={{ color: "primary.main" }} />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={(e) => {
                // Evita double-toggle: sem isso, o clique no "Ações" também dispara onRowClick.
                e.stopPropagation();
                if (modoMultiplo) {
                  handleToggleSelecao(params.row as IComponenteAplicavel);
                } else {
                  setComponenteSelecionado(params.row as IComponenteAplicavel);
                }
              }}
              sx={{
                color: isSelected ? "#1976d2" : "#9e9e9e",
              }}
            >
              <CheckIcon
                sx={{
                  color: isSelected ? "#1976d2" : "#9e9e9e",
                  fontSize: 24,
                }}
              />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
        },
      }}
    >
      <DialogContent dividers sx={{ pt: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Título da seção com borda inferior */}
          <Typography 
            variant="body1" 
            sx={{ 
              color: "#666", 
              pb: 2, 
              borderBottom: "1px solid #e0e0e0",
              mb: 1 
            }}
          >
            Buscar código aplicável
          </Typography>

          {/* Frente 10 (02/10/2026): contador de seleção (só no agrupamento de componentes) */}
          {modoMultiplo && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body2" sx={{ color: "#666" }}>
                {componentesSelecionados.size} código(s) selecionado(s)
              </Typography>
            </Box>
          )}

          {/* Filtros */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
            <Box sx={{ minWidth: 200, maxWidth: 300 }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: "#4db6ac", 
                  fontWeight: 500,
                  display: "block",
                  mb: 0.5,
                  fontSize: "0.75rem"
                }}
              >
                Grupo
              </Typography>
              <FormControl fullWidth variant="outlined">
                <MuiSelect
                  value={filtroGrupo || ""}
                  displayEmpty
                  onChange={(e) => {
                    setFiltroGrupo(e.target.value);
                    setPage(1);
                  }}
                  sx={{
                    bgcolor: "#f5f5f5",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b0b0b0",
                    },
                  }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return <Typography sx={{ color: "#999" }}>Selecione</Typography>;
                    }
                    return gruposOpcoes.find(g => g.value === selected)?.label || "";
                  }}
                >
                  {gruposOpcoes.map((grupo) => (
                    <MenuItem key={grupo.value} value={grupo.value}>
                      {grupo.label}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>
            </Box>

            <TextField
              placeholder="Buscar por código ou descrição"
              fullWidth
              variant="outlined"
              onChange={(e) => debouncedFiltroBusca(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#f5f5f5",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#b0b0b0",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" size="small">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Tabela */}
          <Box sx={{ height: 400, width: "100%" }}>
            <StripedDataGrid
              columns={columns}
              rows={componentesAplicaveis.map((row) => ({
                ...row,
                isSelected: modoMultiplo
                  ? componentesSelecionados.has(row.idComponente)
                  : componenteSelecionado?.idComponente === row.idComponente,
              }))}
              getRowId={(row) => row.idComponente}
              getRowHeight={() => "auto"}
              loading={isLoading}
              paginationMode="server"
              page={page - 1}
              pageSize={pageSize}
              rowCount={totalRecords}
              onPageChange={(newPage: number) => setPage(newPage + 1)}
              onPageSizeChange={(newPageSize: number) => {
                setPageSize(newPageSize);
                setPage(1);
              }}
              checkboxSelection={false}
              disableSelectionOnClick
              sx={{
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                  "&.highlighted": {
                    backgroundColor: "rgba(33, 150, 243, 0.12) !important",
                  },
                },
              }}
              getRowClassName={(params) => {
                return params.row.isSelected ? "highlighted" : "";
              }}
              onRowClick={(params) => {
                if (modoMultiplo) {
                  handleToggleSelecao(params.row as IComponenteAplicavel);
                } else {
                  setComponenteSelecionado(params.row as IComponenteAplicavel);
                }
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isLoading} variant="outlined">
          CANCELAR
        </Button>
        {modoMultiplo ? (
          <Button
            onClick={handleContinuarMultiplo}
            variant="contained"
            disabled={isLoading || componentesSelecionados.size < 1}
          >
            CONTINUAR ({componentesSelecionados.size})
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isLoading || !componenteSelecionado}
          >
            APLICAR
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
